import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, RefreshCw, Ruler, TrendingUp, X } from 'lucide-react';
import { isLocalhostHost } from '@/services/devBridge';
import { reportError } from '@/services/errorReporter';
import { fetchWithRetry, getNetworkContext } from '@/services/network';
import { TourGuide } from './components/TourGuide';
import { PageLoading } from './components/PageLoading';

const DiscoverView = lazy(() => import('./views/discover').then((module) => ({ default: module.DiscoverView })));
const GrowthView = lazy(() => import('./views/growth').then((module) => ({ default: module.GrowthView })));
const IntervalView = lazy(() => import('./views/interval').then((module) => ({ default: module.IntervalView })));
const PermissionView = lazy(() => import('./views/permission').then((module) => ({ default: module.PermissionView })));
const FamilyPiggyBankView = lazy(() => import('./views/family-piggy-bank').then((module) => ({ default: module.FamilyPiggyBankView })));
const SubscriptionBenefitsView = lazy(() => import('./views/subscription-benefits').then((module) => ({ default: module.SubscriptionBenefitsView })));
const SleepBetaView = lazy(() => import('./views/sleep-beta').then((module) => ({ default: module.SleepBetaView })));
const SleepStatusView = lazy(() => import('./views/sleep-status').then((module) => ({ default: module.SleepStatusView })));
const BellyClockView = lazy(() => import('./views/belly-clock').then((module) => ({ default: module.BellyClockView })));
const TodayView = lazy(() => import('./views/today').then((module) => ({ default: module.TodayView })));
const SurveyView = lazy(() => import('./views/survey').then((module) => ({ default: module.SurveyView })));
const WeekView = lazy(() => import('./views/week').then((module) => ({ default: module.WeekView })));

type TabKey = 'today' | 'week' | 'interval' | 'discover';

// UK 관련 타입 정의
type BridgeWindow = Window & {
  dbd_aos?: {
    getUserData?: () => unknown;
  };
  ssckUK?: string;
  ssckBabyIdx?: string;
  ssckFamilyIdx?: string;
  TheBabyfolio?: {
    postMessage?: (message: string) => void;
  };
  webkit?: {
    messageHandlers?: {
      TheBabyfolio?: {
        postMessage?: (message: string) => void;
      };
      finish?: {
        postMessage?: (message: string) => void;
      };
    };
  };
};

type NativeBridgeMessage = {
  type?: string;
  url?: string;
};

// baby_idx 복호화 유틸
const b64urlToBytes = (s: string): Uint8Array => {
  const replaced = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = replaced.length % 4 ? '='.repeat(4 - (replaced.length % 4)) : '';
  const bin = atob(replaced + pad);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
};

const importAesKeyFromUk = async (ukString: string): Promise<CryptoKey> => {
  const enc = new TextEncoder();
  const hash = await crypto.subtle.digest('SHA-256', enc.encode(ukString));
  return crypto.subtle.importKey('raw', hash, { name: 'AES-GCM' }, false, ['decrypt']);
};

const decryptBabyIdxToken = async (token: string, ukString: string): Promise<string> => {
  const parts = token.split('.');
  if (parts.length !== 2) throw new Error('invalid token');
  const [ivB64, ctB64] = parts;
  const iv = b64urlToBytes(ivB64);
  const ct = b64urlToBytes(ctB64);
  const ivBuffer = iv.buffer.slice(iv.byteOffset, iv.byteOffset + iv.byteLength) as ArrayBuffer;
  const ctBuffer = ct.buffer.slice(ct.byteOffset, ct.byteOffset + ct.byteLength) as ArrayBuffer;
  const key = await importAesKeyFromUk(ukString);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivBuffer }, key, ctBuffer);
  return new TextDecoder().decode(pt);
};

const normalizeUk = (value: unknown): string | null => {
  if (value == null) return null;
  const normalized = String(value).replace(/[\r\n]+/g, '').trim();
  return normalized !== '' ? normalized : null;
};

const BETA_STATUS_FETCH_DELAY_MS = 900;
const PULL_REFRESH_THRESHOLD = 72;
const PULL_REFRESH_MAX = 108;
const PULL_REFRESH_HOLD = 52;
const GROWTH_REPORT_PATH = '/growth';
const TOP_GROWTH_BANNER_ENABLED = false;
const SUBSCRIPTION_BENEFITS_PATH = '/subscription-benefits';
const FAMILY_PIGGY_BANK_PATH = '/family-piggy-bank';

const getScrollTop = (): number =>
  window.scrollY
  || document.documentElement.scrollTop
  || document.body.scrollTop
  || 0;

const shouldIgnorePullGesture = (target: EventTarget | null): boolean => {
  const element = target instanceof Element
    ? target
    : target instanceof Node
      ? target.parentElement
      : null;
  return Boolean(element?.closest('.app-header, button, a, input, select, textarea, [role="tab"]'));
};

const shouldShowNativeCloseButton = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return new URLSearchParams(window.location.search).get('backshow')?.toLowerCase() === 'true';
  } catch {
    return false;
  }
};

const isAndroidUserAgent = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /Android/i.test(navigator.userAgent);
};

const isAndroidWebView = (): boolean => {
  if (typeof window === 'undefined' || !isAndroidUserAgent()) {
    return false;
  }

  const bridgeWindow = window as BridgeWindow;
  const userAgent = navigator.userAgent;

  return Boolean(
    bridgeWindow.dbd_aos
    || typeof bridgeWindow.TheBabyfolio?.postMessage === 'function'
    || /;\s?wv\)/i.test(userAgent)
    || /\bVersion\/[\d.]+.*\bChrome\/[\d.]+.*\bMobile Safari\//i.test(userAgent)
  );
};

const canCloseAndroidWebView = (): boolean => {
  if (typeof window === 'undefined' || !isAndroidUserAgent()) {
    return false;
  }

  return typeof (window as BridgeWindow).TheBabyfolio?.postMessage === 'function';
};

const isGrowthPagePath = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.location.pathname === GROWTH_REPORT_PATH
      || new URLSearchParams(window.location.search).get('page') === 'growth';
  } catch {
    return false;
  }
};

const getGrowthPageQueryParam = (...keys: string[]): string | null => {
  if (typeof window === 'undefined' || !isGrowthPagePath()) {
    return null;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of keys) {
      const value = normalizeUk(params.get(key));
      if (value) return value;
    }
  } catch {
    return null;
  }

  return null;
};

const isSubscriptionBenefitsPagePath = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.location.pathname === SUBSCRIPTION_BENEFITS_PATH
      || new URLSearchParams(window.location.search).get('page') === 'subscription-benefits';
  } catch {
    return false;
  }
};

const isFamilyPiggyBankPagePath = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.location.pathname === FAMILY_PIGGY_BANK_PATH
      || new URLSearchParams(window.location.search).get('page') === 'family-piggy-bank';
  } catch {
    return false;
  }
};

const sendNativeMessage = (message: NativeBridgeMessage): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const payload = JSON.stringify({
    type: message.type ?? 'ERROR',
    url: message.url ?? '',
  });
  const bridgeWindow = window as BridgeWindow;

  if (typeof bridgeWindow.TheBabyfolio?.postMessage === 'function') {
    bridgeWindow.TheBabyfolio.postMessage(payload);
    return;
  }

  const iosHandler = bridgeWindow.webkit?.messageHandlers?.TheBabyfolio;
  if (typeof iosHandler?.postMessage === 'function') {
    iosHandler.postMessage(payload);
    return;
  }

  const finishHandler = bridgeWindow.webkit?.messageHandlers?.finish;
  if (message.type === 'CLOSE_WEBVIEW' && typeof finishHandler?.postMessage === 'function') {
    finishHandler.postMessage('');
    return;
  }

  if (typeof window.postMessage === 'function') {
    window.postMessage(payload, window.location.origin);
  }
};

const closeNativeWebView = (): void => {
  sendNativeMessage({ type: 'CLOSE_WEBVIEW', url: '' });
};

const backFromGrowthReport = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  sendNativeMessage({ type: 'PAGE_NAVIGATION', url: '/' });
};

const backFromSubscriptionBenefits = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  sendNativeMessage({ type: 'PAGE_NAVIGATION', url: '/' });
};

const backFromFamilyPiggyBank = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  sendNativeMessage({ type: 'PAGE_NAVIGATION', url: '/' });
};

const toCleanText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed !== '' ? trimmed : null;
};

const getSelectedBabyName = (mypage: any | null, babyIdx: string): string | null => {
  const families = mypage?.data?.families;
  if (!Array.isArray(families)) return null;

  const numericBabyIdx = Number(babyIdx);
  if (!Number.isFinite(numericBabyIdx)) return null;

  for (const family of families) {
    const babies = family?.babies;
    if (!Array.isArray(babies)) continue;

    for (const baby of babies) {
      if (Number(baby?.id) === numericBabyIdx) {
        return toCleanText(baby?.name);
      }
    }
  }

  return null;
};

type GrowthBannerMetric = 'weight' | 'height' | 'head';
type GrowthBannerPercentileBand = 'check' | 'small' | 'average' | 'big' | 'very_big';

type GrowthBannerRecord = {
  recordedAt: string;
  createdAt: string;
  weight: number | null;
  height: number | null;
  head: number | null;
  percentiles: Partial<Record<GrowthBannerMetric, number | null>>;
};

type GrowthBannerVariant = 'empty' | 'stale' | 'delta' | 'recent';
type GrowthBannerTone = 'mint' | 'pink' | 'amber' | 'blue';

type GrowthBannerState = {
  variant: GrowthBannerVariant;
  tone: GrowthBannerTone;
  title: string;
  description: string;
  cta?: string;
  icon: 'trend' | 'ruler';
  showNoticeDot?: boolean;
};

type TopGrowthBannerProps = {
  state: GrowthBannerState;
  onOpenGrowth: () => void;
};

const GROWTH_BANNER_METRICS: GrowthBannerMetric[] = ['weight', 'height', 'head'];
const GROWTH_BANNER_STALE_DAYS = 30;

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const parsed = Number(String(value).trim().replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeGrowthBannerRecord = (value: unknown): GrowthBannerRecord | null => {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const recordedAt = toCleanText(record.recorded_at);
  const weight = toFiniteNumber(record.weight_kg);
  const height = toFiniteNumber(record.height_cm);
  const head = toFiniteNumber(record.head_cm);
  if (!recordedAt || (weight === null && height === null && head === null)) {
    return null;
  }

  const rawPercentiles = record.percentiles && typeof record.percentiles === 'object'
    ? record.percentiles as Record<string, unknown>
    : {};
  const percentiles: Partial<Record<GrowthBannerMetric, number | null>> = {};
  GROWTH_BANNER_METRICS.forEach((metric) => {
    percentiles[metric] = toFiniteNumber(rawPercentiles[metric]);
  });

  return {
    recordedAt,
    createdAt: toCleanText(record.created_at) ?? recordedAt,
    weight,
    height,
    head,
    percentiles,
  };
};

const compareGrowthBannerRecords = (left: GrowthBannerRecord, right: GrowthBannerRecord): number => {
  const recordedCompare = right.recordedAt.localeCompare(left.recordedAt);
  if (recordedCompare !== 0) return recordedCompare;
  return right.createdAt.localeCompare(left.createdAt);
};

const parseGrowthBannerDate = (value: string): Date | null => {
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDaysBetweenGrowthDates = (startValue: string, endValue: string): number | null => {
  const startDate = parseGrowthBannerDate(startValue);
  const endDate = parseGrowthBannerDate(endValue);
  if (!startDate || !endDate) return null;
  return Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / 86400000));
};

const getDaysSinceGrowthDate = (value: string): number | null => {
  const date = parseGrowthBannerDate(value);
  if (!date) return null;
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000));
};

const getGrowthBannerMetricValue = (record: GrowthBannerRecord, metric: GrowthBannerMetric): number | null =>
  record[metric];

const roundGrowthBannerDelta = (value: number): number =>
  Math.round(value * 10) / 10;

const formatGrowthBannerDelta = (value: number): string => {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(1);
};

const formatGrowthBannerPercentile = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(1);

const findGrowthBannerDelta = (
  records: GrowthBannerRecord[]
): { metric: GrowthBannerMetric; amount: number; days: number; percentile: number | null } | null => {
  const metricPriority: GrowthBannerMetric[] = ['weight', 'height', 'head'];

  for (const metric of metricPriority) {
    const metricRecords = records.filter((record) => getGrowthBannerMetricValue(record, metric) !== null);
    if (metricRecords.length < 2) continue;

    const latestRecord = metricRecords[0];
    const previousRecord = metricRecords.find((record) => record.recordedAt !== latestRecord.recordedAt);
    if (!previousRecord) continue;

    const latestValue = getGrowthBannerMetricValue(latestRecord, metric);
    const previousValue = getGrowthBannerMetricValue(previousRecord, metric);
    if (latestValue === null || previousValue === null) continue;

    const amount = roundGrowthBannerDelta(latestValue - previousValue);
    const days = getDaysBetweenGrowthDates(previousRecord.recordedAt, latestRecord.recordedAt);
    if (amount <= 0 || days === null) continue;

    return {
      metric,
      amount,
      days,
      percentile: latestRecord.percentiles[metric] ?? null,
    };
  }

  return null;
};

const getGrowthBannerPercentileBand = (percentile: number): GrowthBannerPercentileBand => {
  if (percentile <= 3) return 'check';
  if (percentile <= 10) return 'small';
  if (percentile < 90) return 'average';
  if (percentile < 97) return 'big';
  return 'very_big';
};

const buildGrowthBannerState = (
  records: GrowthBannerRecord[],
  babyName: string,
  translate: (key: string, options?: Record<string, unknown>) => string
): GrowthBannerState => {
  if (records.length === 0) {
    return {
      variant: 'empty',
      tone: 'mint',
      icon: 'trend',
      title: translate('growth_banner.empty_title', { name: babyName }),
      description: translate('growth_banner.empty_desc'),
      cta: translate('growth_banner.empty_cta', { name: babyName }),
    };
  }

  const latestRecord = records[0];
  const daysSinceLatest = getDaysSinceGrowthDate(latestRecord.recordedAt);
  if (daysSinceLatest !== null && daysSinceLatest >= GROWTH_BANNER_STALE_DAYS) {
    return {
      variant: 'stale',
      tone: 'amber',
      icon: 'ruler',
      showNoticeDot: true,
      title: translate('growth_banner.stale_title', { days: daysSinceLatest }),
      description: translate('growth_banner.stale_desc'),
    };
  }

  const delta = findGrowthBannerDelta(records);
  if (delta) {
    return {
      variant: 'delta',
      tone: 'pink',
      icon: 'trend',
      title: translate(`growth_banner.delta_title_${delta.metric}`, {
        name: babyName,
        days: delta.days,
        amount: formatGrowthBannerDelta(delta.amount),
      }),
      description: delta.percentile === null
        ? translate('growth_banner.delta_desc_generic')
        : translate(`growth_banner.percentile_short.${getGrowthBannerPercentileBand(delta.percentile)}`, {
            percentile: formatGrowthBannerPercentile(delta.percentile),
          }),
    };
  }

  return {
    variant: 'recent',
    tone: 'blue',
    icon: 'trend',
    title: translate('growth_banner.recent_title', { name: babyName }),
    description: translate('growth_banner.recent_desc'),
  };
};

const TopGrowthBanner: React.FC<TopGrowthBannerProps> = ({ state, onOpenGrowth }) => {
  const { t } = useTranslation();
  const Icon = state.icon === 'ruler' ? Ruler : TrendingUp;
  const isExpanded = state.variant === 'empty';

  return (
    <section className="top-growth-banner-zone" aria-label={t('growth_banner.aria_label')}>
      <button
        type="button"
        className={`top-growth-banner top-growth-banner--${state.variant} top-growth-banner--${state.tone}`}
        onClick={onOpenGrowth}
      >
        <div className="top-growth-banner-copy-row">
          <div className="top-growth-banner-icon" aria-hidden="true">
            <Icon size={24} strokeWidth={2.25} />
          </div>
          <div className="min-w-0">
            <p className="top-growth-banner-title">
              {state.title}
              {state.showNoticeDot && <span className="top-growth-banner-dot" aria-hidden="true" />}
            </p>
            <p className="top-growth-banner-desc">
              {state.description}
            </p>
          </div>
        </div>
        {isExpanded && state.cta ? (
          <span className="top-growth-banner-cta">
            {state.cta}
          </span>
        ) : (
          <ChevronRight className="top-growth-banner-chevron" strokeWidth={3} aria-hidden="true" />
        )}
      </button>
    </section>
  );
};

// UK 가져오는 함수
const tryGetUk = (): string | null => {
  if (typeof window === "undefined") return null;
  const w = window as BridgeWindow;

  if (isLocalhostHost(window.location.hostname) && typeof w.ssckUK === 'string' && w.ssckUK.trim() !== '') {
    return normalizeUk(w.ssckUK);
  }

  // 안드로이드 브릿지
  if (w.dbd_aos?.getUserData) {
    try {
      let userData = w.dbd_aos.getUserData();
      if (typeof userData === "string") {
        try {
          userData = JSON.parse(userData);
        } catch {
          // 문자열 그대로일 수 있음
        }
      }

      let uk: unknown = null;
      if (userData && typeof userData === "object") {
        type UserDataObject = Record<string, unknown> & {
          uk?: unknown;
          UK?: unknown;
          userKey?: unknown;
        };
        const obj: UserDataObject = userData as UserDataObject;
        uk = obj.uk ?? obj.UK ?? obj.userKey ?? null;
      } else if (typeof userData === "string") {
        const match = userData.match(/uk=([^&]+)/);
        if (match) {
          uk = match[1];
        } else {
          uk = userData;
        }
      }

      if (uk) return normalizeUk(uk);
    } catch (err) {
      // 브리지 호출이 실패해도 window 전역 주입값 fallback을 계속 확인한다.
    }
  }

  if (typeof w.ssckUK === "string" && w.ssckUK.trim() !== "") {
    return normalizeUk(w.ssckUK);
  }

  return null;
};

// This effect commits only after the initial Suspense content is ready.
const InitialContentReady: React.FC<{ onReady: (ready: boolean) => void }> = ({ onReady }) => {
  useEffect(() => onReady(true), [onReady]);
  return null;
};

export const MainApp: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('today');
  const [uk, setUk] = useState<string | null>(null);
  const [ukChecked, setUkChecked] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [babyIdx, setBabyIdx] = useState<string>('');
  const [babyIdxChecked, setBabyIdxChecked] = useState(false);
  const [myPage, setMyPage] = useState<any>(null);
  const [myPageLoaded, setMyPageLoaded] = useState(false);
  const [growthBannerRecords, setGrowthBannerRecords] = useState<GrowthBannerRecord[]>([]);
  const [growthBannerLoaded, setGrowthBannerLoaded] = useState(false);
  const [loadingOverlayVisible, setLoadingOverlayVisible] = useState(true);
  const [initialContentReady, setInitialContentReady] = useState(false);
  const [showBeta, setShowBeta] = useState(() => {
    try {
      return window.location.pathname === '/sleep-beta'
        || new URLSearchParams(window.location.search).get('page') === 'sleep-beta';
    } catch { return false; }
  });
  const [showSleepStatus, setShowSleepStatus] = useState(false);
  const [showBellyClock, setShowBellyClock] = useState(false);
  const [showPermissionPage] = useState(() => {
    try {
      return window.location.pathname === '/permission';
    } catch {
      return false;
    }
  });
  const [showSurvey, setShowSurvey] = useState(() => {
    try {
      return window.location.pathname === '/survey'
        || new URLSearchParams(window.location.search).get('page') === 'survey';
    } catch { return false; }
  });
  const [familyIdx, setFamilyIdx] = useState<string>('');
  const [betaRegistered, setBetaRegistered] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isAndroidHeaderCompact = isAndroidUserAgent();
  const isAndroidNativeWebView = isAndroidWebView();
  const showNativeCloseButton = shouldShowNativeCloseButton() || canCloseAndroidWebView();
  const showNativeWebViewUi = showNativeCloseButton || isAndroidNativeWebView;
  const showGrowthPage = isGrowthPagePath();
  const showSubscriptionBenefitsPage = isSubscriptionBenefitsPagePath();
  const showFamilyPiggyBankPage = isFamilyPiggyBankPagePath();

  const touchStartYRef = useRef<number | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const pullCandidateRef = useRef(false);
  const refreshTimerRef = useRef<number | null>(null);

  // i18n 기반 문서 타이틀 및 언어 동적 업데이트
  useEffect(() => {
    const baseTitle = t('common.app_title');
    
    // HTML lang 속성 업데이트
    const resolvedLanguage = (i18n.resolvedLanguage ?? i18n.language ?? 'ko')
      .toLowerCase()
      .replace(/_/g, '-')
      .split('-')[0];
    const currentLang = resolvedLanguage === 'en' || resolvedLanguage === 'ja'
      ? resolvedLanguage
      : 'ko';
    document.documentElement.lang = currentLang;

    if (!ukChecked) {
      document.title = `${t('common.loading')} | ${baseTitle}`;
      return;
    }

    if (!uk) {
      document.title = `${t('common.app_only_title')} | ${baseTitle}`;
      return;
    }

    if (showSleepStatus) {
      document.title = `${t('sleep.status_title')} | ${baseTitle}`;
      return;
    }

    if (showBellyClock) {
      document.title = `${t('belly_clock.title')} | ${baseTitle}`;
      return;
    }

    if (showPermissionPage) {
      document.title = `${t('permission.title')} | ${baseTitle}`;
      return;
    }

    if (showGrowthPage) {
      document.title = `${t('growth_page.title')} | ${baseTitle}`;
      return;
    }

    if (showSubscriptionBenefitsPage) {
      document.title = `${t('subscription_benefits.title')} | ${baseTitle}`;
      return;
    }

    if (showFamilyPiggyBankPage) {
      document.title = `${t('family_piggy_bank.title')} | ${baseTitle}`;
      return;
    }

    const titleByTab: Record<TabKey, string> = {
      today: t('tabs.today'),
      week: t('tabs.week'),
      interval: t('tabs.interval'),
      discover: t('tabs.discover'),
    };
    document.title = `${titleByTab[activeTab]} | ${baseTitle}`;
  }, [activeTab, uk, ukChecked, showSleepStatus, showBellyClock, showPermissionPage, showGrowthPage, showSubscriptionBenefitsPage, showFamilyPiggyBankPage, i18n.language, i18n.resolvedLanguage, t]);

  useEffect(() => {
    let acquired = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const MAX_TRIES = 20;
    const DELAY_MS = 150;

    // A small entry bundle may run before the native app injects its credentials.
    const acquireUk = () => {
      if (acquired) return;
      clearTimeout(timer);
      let attempts = 0;
      const check = () => {
        const got = tryGetUk();
        if (got) {
          acquired = true;
          setUk(got);
          setUkChecked(true);
          return;
        }
        if (++attempts < MAX_TRIES) timer = setTimeout(check, DELAY_MS);
        else setUkChecked(true);
      };
      check();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') acquireUk();
    };

    acquireUk();
    window.addEventListener('focus', acquireUk);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', acquireUk);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!uk) return;
    let canceled = false;
    const MAX_TRIES = 10;
    const DELAY_MS = 200;

    const run = async () => {
      const u = new URL(window.location.href);
      const growthQueryBabyIdx = getGrowthPageQueryParam('baby_idx', 'babyIdx');
      if (growthQueryBabyIdx) {
        setBabyIdx(growthQueryBabyIdx);
        setBabyIdxChecked(true);
        return;
      }

      const token = u.searchParams.get('babykey') || u.searchParams.get('babyKey');

      if (token && uk) {
        try {
          const decrypted = await decryptBabyIdxToken(token, uk);
          if (decrypted) {
            setBabyIdx(decrypted);
            setBabyIdxChecked(true);
            return;
          }
        } catch {}
      }

      for (let i = 0; i < MAX_TRIES && !canceled; i++) {
        const w = window as BridgeWindow;
        if (w.ssckBabyIdx) {
          setBabyIdx(w.ssckBabyIdx);
          setBabyIdxChecked(true);
          return;
        }
        await new Promise(r => setTimeout(r, DELAY_MS));
      }

      if (!canceled) {
        setBabyIdx('');
        setBabyIdxChecked(true);
      }
    };

    run();
    return () => { canceled = true; };
  }, [uk]);

  // 마이페이지 호출 함수
  const fetchMyPage = async (baby_idx_value: string, uk_value: string, signal: AbortSignal) => {
    const url = `/api/account-mypage?baby_idx=${encodeURIComponent(baby_idx_value)}`;
    try {
      const resp = await fetchWithRetry(url, {
        method: 'GET',
        cache: 'no-store',
        signal,
      }, {
        authToken: uk_value,
      });
      if (!resp.ok) throw new Error('Failed to fetch mypage');
      const data = await resp.json();
      if (!signal.aborted) setMyPage(data);
    } catch (e) {
      if (signal.aborted) return;
      reportError('Failed to fetch mypage', e, {
        babyIdx: baby_idx_value || 'none',
        requestUrl: url,
        hasUk: Boolean(uk_value),
        ...getNetworkContext(),
      });
      // 실패해도 UI 차단 없이 진행
      setMyPage(null);
    } finally {
      if (!signal.aborted) setMyPageLoaded(true);
    }
  };

  useEffect(() => {
    if (!uk || !babyIdxChecked) return;
    if (!babyIdx && !showBeta && !showSurvey && !showGrowthPage && !showSubscriptionBenefitsPage && !showFamilyPiggyBankPage) return;
    const controller = new AbortController();
    const safeBabyIdx = (babyIdx && familyIdx && babyIdx === familyIdx) ? '' : babyIdx;
    setMyPageLoaded(false);
    // Keep the existing grace period only when the optional family bridge is absent.
    // Older clients may never provide it; waiting for the full polling window adds 2s.
    const timer = familyIdx
      ? undefined
      : setTimeout(() => {
          const injectedFamilyIdx = (window as BridgeWindow).ssckFamilyIdx;
          if (injectedFamilyIdx) {
            setFamilyIdx(injectedFamilyIdx);
            return;
          }
          void fetchMyPage(safeBabyIdx || '', uk, controller.signal);
        }, 400);
    if (familyIdx) void fetchMyPage(safeBabyIdx || '', uk, controller.signal);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [uk, babyIdxChecked, babyIdx, showBeta, showSurvey, showGrowthPage, showSubscriptionBenefitsPage, showFamilyPiggyBankPage, familyIdx]);

  useEffect(() => {
    const canLoadGrowthBanner = Boolean(
      TOP_GROWTH_BANNER_ENABLED
      && showNativeWebViewUi
      && uk
      && babyIdx
      && babyIdxChecked
      && !showBeta
      && !showSleepStatus
      && !showSurvey
      && !showGrowthPage
      && !showSubscriptionBenefitsPage
      && !showFamilyPiggyBankPage
      && activeTab === 'today'
    );

    if (!canLoadGrowthBanner || !uk) {
      setGrowthBannerRecords([]);
      setGrowthBannerLoaded(false);
      return;
    }

    let canceled = false;
    const requestUrl = `/api/growth-records?baby_idx=${encodeURIComponent(babyIdx)}&limit=8`;
    setGrowthBannerLoaded(false);

    const loadGrowthBannerRecords = async () => {
      try {
        const response = await fetchWithRetry(requestUrl, {
          method: 'GET',
          cache: 'no-store',
        }, {
          authToken: uk,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch growth banner records (${response.status})`);
        }

        const payload = await response.json() as { data?: { records?: unknown[] } };
        const records = Array.isArray(payload?.data?.records)
          ? payload.data.records
              .map(normalizeGrowthBannerRecord)
              .filter((record): record is GrowthBannerRecord => record !== null)
              .sort(compareGrowthBannerRecords)
          : [];

        if (!canceled) {
          setGrowthBannerRecords(records);
          setGrowthBannerLoaded(true);
        }
      } catch (error) {
        if (!canceled) {
          reportError('Failed to load growth banner records', error, {
            babyIdx: babyIdx || 'none',
            requestUrl,
            hasUk: Boolean(uk),
            ...getNetworkContext(),
          });
          setGrowthBannerRecords([]);
          setGrowthBannerLoaded(false);
        }
      }
    };

    loadGrowthBannerRecords();

    return () => {
      canceled = true;
    };
  }, [
    showNativeWebViewUi,
    uk,
    babyIdx,
    babyIdxChecked,
    activeTab,
    showBeta,
    showSleepStatus,
    showSurvey,
    showGrowthPage,
    showSubscriptionBenefitsPage,
    showFamilyPiggyBankPage,
  ]);

  useEffect(() => {
    if (!uk) return;
    let canceled = false;
    const MAX_TRIES = 10;
    const DELAY_MS = 200;
    const run = async () => {
      for (let i = 0; i < MAX_TRIES && !canceled; i++) {
        const w = window as BridgeWindow;
        if (w.ssckFamilyIdx) { setFamilyIdx(w.ssckFamilyIdx); return; }
        await new Promise(r => setTimeout(r, DELAY_MS));
      }
    };
    run();
    return () => { canceled = true; };
  }, [uk]);

  useEffect(() => {
    if (!uk || !babyIdx) return;
    const requestUrl = `/api/beta-status?baby_idx=${encodeURIComponent(babyIdx)}`;
    let succeeded = false;

    const checkBetaStatus = async (): Promise<boolean> => {
      try {
        const resp = await fetchWithRetry(requestUrl, {
          method: 'GET',
          cache: 'no-store',
        }, {
          authToken: uk,
          skipCooldownCheck: true,
        });
        if (resp.ok) {
          const json = await resp.json();
          setBetaRegistered(json?.data?.registered === true);
          return true;
        }
        reportError('Beta status returned non-OK', null, {
          status: resp.status,
          babyIdx,
          requestUrl,
        });
      } catch (error) {
        reportError('Failed to check beta status', error, {
          babyIdx,
          requestUrl,
          ...getNetworkContext(),
        });
      }
      return false;
    };

    const id = window.setTimeout(async () => {
      succeeded = await checkBetaStatus();
    }, BETA_STATUS_FETCH_DELAY_MS);

    const onVisibilityChange = async () => {
      if (!succeeded && document.visibilityState === 'visible') {
        succeeded = await checkBetaStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearTimeout(id);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [uk, babyIdx]);

  const rawAccountIdx = myPage?.data?.channel_id ?? null;
  const accountIdx = rawAccountIdx != null ? Number(rawAccountIdx) : null;

  const isSleepServiceAllowed = betaRegistered;

  const invalidBabyData = !babyIdx || !familyIdx || babyIdx === familyIdx;

  const hasEligibleBaby = useMemo(() => {
    const families = myPage?.data?.families;
    if (!families || !Array.isArray(families)) return false;


    const isInRange = (birthday: string) => !isNaN(new Date(birthday).getTime());

    if (!invalidBabyData) {
      const numBabyIdx = Number(babyIdx);
      const family = families.find((f: any) =>
        (f.type === 'M' || f.type === 'P') &&
        (f.babies ?? []).some((b: any) => b.id === numBabyIdx)
      );
      if (!family) return false;
      const baby = family.babies.find((b: any) => b.id === numBabyIdx);
      return baby?.birthday ? isInRange(baby.birthday) : false;
    }

    return families
      .filter((f: any) => f.type === 'M' || f.type === 'P')
      .some((f: any) =>
        (f.babies ?? []).some((b: any) => b.birthday && isInRange(b.birthday))
      );
  }, [myPage, invalidBabyData, babyIdx]);

  const showBetaBanner = betaRegistered || hasEligibleBaby;

  useEffect(() => {
    if (showBeta && myPageLoaded && !showBetaBanner) {
      if (!babyIdx || !familyIdx || babyIdx === familyIdx) return;
      setShowBeta(false);
    }
  }, [showBeta, myPageLoaded, showBetaBanner, babyIdx, familyIdx]);

  useEffect(() => {
    if (showSleepStatus && myPageLoaded && !isSleepServiceAllowed) {
      setShowSleepStatus(false);
    }
  }, [showSleepStatus, myPageLoaded, isSleepServiceAllowed]);

  const isLoading = !ukChecked || Boolean(uk && !initialContentReady);

  useEffect(() => {
    let timeoutId: number | undefined;
    if (isLoading) {
      setLoadingOverlayVisible(true);
    } else {
      timeoutId = window.setTimeout(() => setLoadingOverlayVisible(false), 200);
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isLoading]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const pullRefreshEnabled = Boolean(
    uk
    && !isLoading
    && !showBeta
    && !showSleepStatus
    && !showSurvey
    && !showGrowthPage
    && !showSubscriptionBenefitsPage
    && !showFamilyPiggyBankPage
    && (activeTab === 'today' || activeTab === 'week' || activeTab === 'interval')
  );

  const pullProgress = Math.min(1, pullDistance / PULL_REFRESH_THRESHOLD);
  const showPullIndicator = pullRefreshEnabled && (pullDistance > 0 || isRefreshing);
  const selectedBabyName = getSelectedBabyName(myPage, babyIdx) ?? t('growth_banner.default_baby_name');
  const growthBannerState = useMemo(
    () => buildGrowthBannerState(
      growthBannerRecords,
      selectedBabyName,
      (key, options) => t(key, options)
    ),
    [growthBannerRecords, selectedBabyName, t]
  );
  const showTopGrowthBanner = Boolean(
    TOP_GROWTH_BANNER_ENABLED
    && showNativeWebViewUi
    && babyIdx
    && babyIdxChecked
    && growthBannerLoaded
    && myPageLoaded
    && activeTab === 'today'
  );

  const openGrowthReport = () => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams();
    if (babyIdx) {
      params.set('baby_idx', babyIdx);
    }

    window.location.assign(`${GROWTH_REPORT_PATH}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const resetPullGesture = () => {
    pullCandidateRef.current = false;
    touchStartYRef.current = null;
    touchStartXRef.current = null;
  };

  const selectTab = (tab: TabKey) => {
    resetPullGesture();
    setPullDistance(0);
    setActiveTab(tab);
  };

  const handlePullTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!pullRefreshEnabled || isRefreshing || event.touches.length !== 1) return;
    if (shouldIgnorePullGesture(event.target)) return;
    if (getScrollTop() > 0) return;

    const touch = event.touches[0];
    touchStartYRef.current = touch.clientY;
    touchStartXRef.current = touch.clientX;
    pullCandidateRef.current = true;
  };

  const handlePullTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!pullRefreshEnabled || isRefreshing || !pullCandidateRef.current) return;
    if (touchStartYRef.current === null || touchStartXRef.current === null) return;

    if (getScrollTop() > 0) {
      resetPullGesture();
      setPullDistance(0);
      return;
    }

    const touch = event.touches[0];
    const deltaY = touch.clientY - touchStartYRef.current;
    const deltaX = Math.abs(touch.clientX - touchStartXRef.current);

    if (deltaY <= 0 || deltaX > deltaY) {
      if (pullDistance !== 0) {
        setPullDistance(0);
      }
      return;
    }

    event.preventDefault();
    setPullDistance(Math.min(PULL_REFRESH_MAX, deltaY * 0.45));
  };

  const handlePullTouchEnd = () => {
    if (!pullRefreshEnabled) {
      resetPullGesture();
      return;
    }

    const shouldRefresh = pullDistance >= PULL_REFRESH_THRESHOLD;
    resetPullGesture();

    if (!shouldRefresh) {
      setPullDistance(0);
      return;
    }

    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
    }

    setIsRefreshing(true);
    setPullDistance(PULL_REFRESH_HOLD);
    setRefreshNonce((prev) => prev + 1);

    refreshTimerRef.current = window.setTimeout(() => {
      setIsRefreshing(false);
      setPullDistance(0);
      refreshTimerRef.current = null;
    }, 900);
  };

  // 정상적으로 UK를 받았을 때만 메인 UI 렌더링
  if (showPermissionPage) {
    return <PermissionView />;
  }

  return (
    <>
      {showPullIndicator && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-40 flex justify-center">
          <div
            className="mt-2 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-pink-500 shadow-md ring-1 ring-black/5"
            style={{
              opacity: isRefreshing ? 1 : Math.max(0.35, pullProgress),
              transform: `translateY(${Math.min(20, 6 + pullDistance * 0.22)}px) scale(${0.92 + (pullProgress * 0.08)})`,
              transition: isRefreshing ? 'transform 0.2s ease, opacity 0.2s ease' : 'opacity 0.1s linear',
            }}
            aria-hidden="true"
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
              style={{ transform: isRefreshing ? undefined : `rotate(${pullProgress * 180}deg)` }}
            />
          </div>
        </div>
      )}

      {loadingOverlayVisible && (
        <div
          className="fixed inset-0 z-[998] flex items-center justify-center"
          style={{
            opacity: isLoading ? 1 : 0,
            pointerEvents: isLoading ? 'auto' : 'none',
            transition: 'opacity 0.2s ease',
            background: '#F9FAFB',
          }}
        >
          <PageLoading />
        </div>
      )}

      {showTour && (
        <TourGuide onClose={() => setShowTour(false)} gotoTab={(t) => setActiveTab(t)} />
      )}
      
      {/* 메인 콘텐츠는 로딩 상태에 맞춰 점진적으로 노출 */}
      <div
        className="app-shell"
        aria-hidden={isLoading}
        onTouchStart={handlePullTouchStart}
        onTouchMove={handlePullTouchMove}
        onTouchEnd={handlePullTouchEnd}
        onTouchCancel={handlePullTouchEnd}
        style={{
          opacity: isLoading ? 0 : 1,
          transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined,
          transition: isRefreshing || pullDistance === 0
            ? 'transform 0.2s ease'
            : undefined,
          pointerEvents: isLoading ? 'none' : 'auto',
          willChange: pullDistance > 0 ? 'transform' : undefined,
        }}
      >
        {uk ? (
          <>
            {!showBeta && !showSleepStatus && !showBellyClock && !showSurvey && !showGrowthPage && !showSubscriptionBenefitsPage && !showFamilyPiggyBankPage && (
              <header
                className={`app-header${isAndroidHeaderCompact ? ' app-header--android' : ''}`}
                onTouchStart={(event) => event.stopPropagation()}
              >
                <div className="app-header-inner">
                  <div className="app-header-tabs-row">
                    {showNativeCloseButton && (
                      <button
                        type="button"
                        className="tab-close-btn"
                        aria-label={t('common.close', '닫기')}
                        onClick={closeNativeWebView}
                      >
                        <X size={20} strokeWidth={2.5} aria-hidden="true" />
                      </button>
                    )}
                    <nav className="tabs" role="tablist" aria-label={t('tabs.aria_label', '상단 탭')} data-tour="top-tabs">
                      <button
                        className="tab-btn"
                        role="tab"
                        aria-selected={activeTab === 'today'}
                        onTouchStart={(event) => {
                          event.stopPropagation();
                          selectTab('today');
                        }}
                        onClick={() => selectTab('today')}
                      >
                        {t('tabs.today')}
                      </button>
                      <button
                        className="tab-btn"
                        role="tab"
                        aria-selected={activeTab === 'week'}
                        onTouchStart={(event) => {
                          event.stopPropagation();
                          selectTab('week');
                        }}
                        onClick={() => selectTab('week')}
                      >
                        {t('tabs.week')}
                      </button>
                      <button
                        className="tab-btn"
                        role="tab"
                        aria-selected={activeTab === 'interval'}
                        onTouchStart={(event) => {
                          event.stopPropagation();
                          selectTab('interval');
                        }}
                        onClick={() => selectTab('interval')}
                      >
                        {t('tabs.interval')}
                      </button>
                      <button
                        className="tab-btn"
                        role="tab"
                        aria-selected={activeTab === 'discover'}
                        onTouchStart={(event) => {
                          event.stopPropagation();
                          selectTab('discover');
                        }}
                        onClick={() => selectTab('discover')}
                        data-tour="tab-discover"
                      >
                        {t('tabs.discover')}
                      </button>
                    </nav>
                  </div>
                </div>
              </header>
            )}
            {!showBeta && !showSleepStatus && !showBellyClock && !showSurvey && !showGrowthPage && !showSubscriptionBenefitsPage && !showFamilyPiggyBankPage && showTopGrowthBanner && (
              <TopGrowthBanner state={growthBannerState} onOpenGrowth={openGrowthReport} />
            )}

            <Suspense fallback={initialContentReady ? <PageLoading /> : null}>
            <InitialContentReady onReady={setInitialContentReady} />
            {showSubscriptionBenefitsPage ? (
              <SubscriptionBenefitsView
                uk={uk}
                babyIdx={babyIdx}
                familyIdx={familyIdx}
                onBack={backFromSubscriptionBenefits}
              />
            ) : showFamilyPiggyBankPage ? (
              <FamilyPiggyBankView
                uk={uk}
                familyIdx={familyIdx}
                myPage={myPageLoaded ? myPage : null}
                onBack={backFromFamilyPiggyBank}
              />
            ) : showGrowthPage ? (
              <GrowthView
                uk={uk}
                babyIdx={babyIdx}
                mypage={myPageLoaded ? myPage : null}
                onBack={backFromGrowthReport}
              />
            ) : showBeta ? (
              <SleepBetaView
                uk={uk}
                babyIdx={babyIdx}
                onBack={() => setShowBeta(false)}
                onOpenSleepStatus={isSleepServiceAllowed ? () => { setShowBeta(false); setShowSleepStatus(true); } : undefined}
              />
            ) : showSleepStatus ? (
              <SleepStatusView
                uk={uk}
                babyIdx={babyIdx}
                onBack={() => setShowSleepStatus(false)}
              />
            ) : showBellyClock ? (
              <BellyClockView
                onBack={() => setShowBellyClock(false)}
              />
            ) : showSurvey ? (
              <SurveyView
                uk={uk}
                accountIdx={accountIdx}
                onBack={() => setShowSurvey(false)}
              />
            ) : (
              <>
                <main className="content">
                  {activeTab === 'today' && (
                    <TodayView
                      uk={uk}
                      babyIdx={babyIdx}
                      babyIdxChecked={babyIdxChecked}
                      mypage={myPageLoaded ? myPage : null}
                      refreshNonce={refreshNonce}
                    />
                  )}
                  {activeTab === 'week' && (
                    <WeekView
                      uk={uk}
                      babyIdx={babyIdx}
                      babyIdxChecked={babyIdxChecked}
                      mypage={myPageLoaded ? myPage : null}
                      refreshNonce={refreshNonce}
                    />
                  )}
                  {activeTab === 'interval' && (
                    <IntervalView
                      uk={uk}
                      babyIdx={babyIdx}
                      babyIdxChecked={babyIdxChecked}
                      mypage={myPageLoaded ? myPage : null}
                      refreshNonce={refreshNonce}
                    />
                  )}
                  {activeTab === 'discover' && (
                    <DiscoverView
                      onOpenBeta={betaRegistered ? undefined : (showBetaBanner ? () => setShowBeta(true) : undefined)}
                      onOpenSleepStatus={betaRegistered ? () => setShowSleepStatus(true) : undefined}
                      onOpenBellyClock={() => setShowBellyClock(true)}
                    />
                  )}
                </main>

                {/* 꿀잠타이밍 플로팅 버튼 (오늘/주간 탭, 베타 신청자 전용) */}
                {betaRegistered && (activeTab === 'today' || activeTab === 'week') && (
                  <button
                    onClick={() => setShowSleepStatus(true)}
                    className="fixed bottom-6 right-5 z-40 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-2xl transition-all duration-200 hover:scale-110 active:scale-95"
                    style={{
                      background: 'linear-gradient(135deg, #7C5CB8 0%, #6A8DD6 100%)',
                      boxShadow: '0 4px 16px rgba(124,92,184,0.4)',
                    }}
                    aria-label={t('beta.sleep_banner_title')}
                  >
                    🍯
                  </button>
                )}
              </>
            )}
            </Suspense>
          </>
        ) : (
          <div className="content content-safe flex items-center justify-center min-h-screen">
            <div className="text-center">
              <p className="text-lg font-semibold">{t('common.app_only_title')}</p>
              <p className="text-[15px] text-gray-500 mt-2">{t('common.app_only_desc')}</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
